/**
 * Validation helper functions for form fields
 */
/**
 * Validates email format
 */
export const validateEmail = (email) => {
    if (!email || email.trim() === '') {
        return {
            isValid: false,
            error: 'Email is required',
        };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return {
            isValid: false,
            error: 'Please enter a valid email address',
        };
    }
    return { isValid: true };
};
/**
 * A simple password validation (legacy) - some users perhaps have passwords without capital
 * letters or digits like in {@link validatePasswordStrength}, immediatelly switching to new
 * validation method would block some users from logging in
 */
export const validatePassword = (password) => {
    if (!password || password.trim() === '') {
        return {
            isValid: false,
            error: 'Password is required',
        };
    }
    if (password.length < 6) {
        return {
            isValid: false,
            error: 'Password must be at least 6 characters',
        };
    }
    return { isValid: true };
};
/**
 * Validates password strength with detailed requirements
 * Requirements:
 * - At least 1 letter (a-z, A-Z)
 * - At least 1 digit (0-9)
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 */
export const validatePasswordStrength = (password) => {
    const rules = [
        {
            key: 'letter',
            label: 'Atleast 1 letter required',
            test: (pwd) => /[a-zA-Z]/.test(pwd),
        },
        {
            key: 'digit',
            label: 'Atleast 1 digit required (0-9)',
            test: (pwd) => /\d/.test(pwd),
        },
        {
            key: 'length',
            label: 'Minimum 8 characters',
            test: (pwd) => pwd.length >= 8,
        },
        {
            key: 'uppercase',
            label: 'Atleast 1 uppercase letter required',
            test: (pwd) => /[A-Z]/.test(pwd),
        },
    ];
    const rulesWithResults = rules.map((rule) => (Object.assign(Object.assign({}, rule), { passed: rule.test(password) })));
    const allPassed = rulesWithResults.every((rule) => rule.passed);
    return {
        isValid: allPassed,
        rules: rulesWithResults,
    };
};
/**
 * Validates login form data
 */
export const validateLoginForm = (email, password) => {
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    const errors = {};
    if (!emailValidation.isValid) {
        errors.email = emailValidation.error;
    }
    if (!passwordValidation.isValid) {
        errors.password = passwordValidation.error;
    }
    return {
        isValid: emailValidation.isValid && passwordValidation.isValid,
        errors,
    };
};
/**
 * Extracts the domain from an email address
 * @param email - The email address
 * @returns The domain part (e.g., 'gmail.com') or empty string if invalid
 */
export const getEmailDomain = (email) => {
    const domain = email.split('@')[1];
    return domain ? domain.toLowerCase() : '';
};
/**
 * Gets the webmail inbox URL for a given email address
 * @param email - The email address
 * @returns The inbox URL for the email provider or null if the email provider is not found
 */
export const getEmailProviderInboxUrl = (email) => {
    const emailDomain = getEmailDomain(email);
    if (!emailDomain) {
        return null;
    }
    if (emailDomain.includes('gmail.com')) {
        return 'https://mail.google.com';
    }
    else if (emailDomain.includes('outlook.com') || emailDomain.includes('hotmail.com') || emailDomain.includes('live.com')) {
        return 'https://outlook.live.com/mail';
    }
    else if (emailDomain.includes('yahoo.com')) {
        return 'https://mail.yahoo.com';
    }
    else if (emailDomain.includes('icloud.com') || emailDomain.includes('me.com')) {
        return 'https://www.icloud.com/mail';
    }
    else if (emailDomain.includes('aol.com')) {
        return 'https://mail.aol.com';
    }
    else if (emailDomain.includes('protonmail.com') || emailDomain.includes('proton.me')) {
        return 'https://mail.proton.me';
    }
    else {
        return null;
    }
};
